import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { ChevronLeft } from '../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { Button } from '../Button';
import { s as buttonStyles } from '../Button/styles';
import { s } from './styles';

function Calendar({
	styles = [],
	classNames,
	showOutsideDays = true,
	captionLayout = 'label',
	buttonVariant = 'ghost',
	formatters,
	components,
	...props
}: React.ComponentProps<typeof DayPicker> & {
	buttonVariant?: React.ComponentProps<typeof Button>['variant'];
	styles?: StyleDeclarationValue[];
}) {
	const defaultClassNames = getDefaultClassNames();
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={css(s.calendar, styles)}
			captionLayout={captionLayout}
			formatters={{
				formatMonthDropdown: date => date.toLocaleString('default', { month: 'short' }),
				...formatters,
			}}
			classNames={{
				root: `${css(s.root)} ${defaultClassNames.root}`,
				months: `${css(s.months)} ${defaultClassNames.months}`,
				month: `${css(s.month)} ${defaultClassNames.month}`,
				nav: `${css(s.nav)} ${defaultClassNames.nav}`,
				button_previous: `${css(buttonStyles.base, buttonStyles[buttonVariant], s.navButton)} ${defaultClassNames.button_previous}`,
				button_next: `${css(buttonStyles.base, buttonStyles[buttonVariant], s.navButton)} ${defaultClassNames.button_next}`,
				month_caption: `${css(s.monthCaption)} defaultClassNames.month_caption`,
				dropdowns: `${css(s.dropdowns)} ${defaultClassNames.dropdowns}`,
				dropdown_root: `${css(s.dropdownRoot)} ${defaultClassNames.dropdown_root}`,
				dropdown: `${css(s.dropdown)} ${defaultClassNames.dropdown}`,
				caption_label: `${css(
					s.captionLabel,
					captionLayout === 'label' ? s.captionLabelText : s.captionLabelButton
				)} ${defaultClassNames.caption_label}`,
				table: css(s.table),
				weekdays: `${css(s.weekdays)} ${defaultClassNames.weekdays}`,
				weekday: `${css(s.weekday)} ${defaultClassNames.weekday}`,
				week: `${css(s.week)} ${defaultClassNames.week}`,
				week_number_header: `${css(s.weekNumberHeader)} ${defaultClassNames.week_number_header}`,
				week_number: `${css(s.weekNumber)} ${defaultClassNames.week_number}`,
				day: `${css(s.day)} ${defaultClassNames.day}`,
				range_start: `${css(s.rangeStart)} ${defaultClassNames.range_start}`,
				range_middle: `${css(s.rangeMiddle)} ${defaultClassNames.range_middle}`,
				range_end: `${css(s.rangeEnd)} ${defaultClassNames.range_end}`,
				today: `${css(s.today)} ${defaultClassNames.today}`,
				outside: `${css(s.outside)} ${defaultClassNames.outside}`,
				disabled: `${css(s.disabled)} ${defaultClassNames.disabled}`,
				hidden: `${css(s.hidden)} ${defaultClassNames.hidden}`,
				...classNames,
			}}
			components={{
				Root: ({ className, rootRef, ...rest }) => {
					return <div data-slot='calendar' ref={rootRef} className={className} {...rest} />;
				},
				Chevron: ({ className, orientation, ...rest }) => {
					if (orientation === 'left') {
						return <ChevronLeft className={className} width={16} height={16} {...rest} />;
					}
					if (orientation === 'right') {
						return <ChevronRight className={className} width={16} height={16} {...rest} />;
					}
					return <ChevronDown className={className} width={16} height={16} {...rest} />;
				},
				DayButton: CalendarDayButton,
				WeekNumber: ({ children, ...rest }) => {
					return (
						<td {...rest}>
							<div className={css(s.weekNumberCell)}>{children}</div>
						</td>
					);
				},
				...components,
			}}
			{...props}
		/>
	);
}
function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
	const defaultClassNames = getDefaultClassNames();
	const ref = React.useRef<HTMLButtonElement>(null);
	React.useEffect(() => {
		if (modifiers.focused) ref.current?.focus();
	}, [modifiers.focused]);

	return (
		<Button
			ref={ref}
			variant='ghost'
			size='icon'
			data-day={day.date.toLocaleDateString()}
			data-selected-single={
				modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
			}
			data-range-start={modifiers.range_start}
			data-range-end={modifiers.range_end}
			data-range-middle={modifiers.range_middle}
			styles={[s.calendarDayButton]}
			selectorClassName={`calendar-day-button ${defaultClassNames.day} ${className}`}
			{...props}
		/>
	);
}
export { Calendar, CalendarDayButton };
